import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  DEFAULT_CERT_NOTIFY_OFFSET_DAYS,
  getStoredUserProfile,
  normalizeUserProfile,
  setStoredUserProfile,
} from '../utils/userProfile';
import { fetchUserProfile, saveUserProfile as saveUserProfileApi } from '../utils/userProfileApi';

const UserProfileContext = createContext(null);

export function UserProfileProvider({ email, children }) {
  const [profile, setProfile] = useState(() => getStoredUserProfile(email));
  const [loading, setLoading] = useState(Boolean(email));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!email) {
      setProfile(normalizeUserProfile());
      setLoading(false);
      return;
    }

    let cancelled = false;
    const cached = getStoredUserProfile(email);
    setProfile(cached);
    setLoading(true);

    (async () => {
      const remote = await fetchUserProfile(email);
      if (cancelled) return;
      if (remote) {
        setProfile(remote);
        setStoredUserProfile(email, remote);
      }
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [email]);

  const saveProfile = useCallback(
    async (next) => {
      const value = normalizeUserProfile(next);
      setSaving(true);
      try {
        const saved = await saveUserProfileApi(email, value);
        setStoredUserProfile(email, saved);
        setProfile(saved);
        return saved;
      } finally {
        setSaving(false);
      }
    },
    [email]
  );

  const value = useMemo(
    () => ({
      name: profile.name,
      signature: profile.signature,
      certificateNotifyOffsetDays:
        profile.certificate_notify_offset_days ?? DEFAULT_CERT_NOTIFY_OFFSET_DAYS,
      loading,
      saving,
      saveProfile,
    }),
    [
      profile.name,
      profile.signature,
      profile.certificate_notify_offset_days,
      loading,
      saving,
      saveProfile,
    ]
  );

  return <UserProfileContext.Provider value={value}>{children}</UserProfileContext.Provider>;
}

export function useUserProfile() {
  const ctx = useContext(UserProfileContext);
  if (!ctx) {
    return {
      name: '',
      signature: '',
      certificateNotifyOffsetDays: DEFAULT_CERT_NOTIFY_OFFSET_DAYS,
      loading: false,
      saving: false,
      saveProfile: async () => {},
    };
  }
  return ctx;
}
